import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { 
  MY_CHAMPIONS_LEAGUE_GAME_DATA, 
  CHAMPIONS_LEAGUE_TEAMS, 
  CHAMPIONS_LEAGUE_SETTINGS 
} from '../../../graphql/queries';
import { 
  setPhaseLocks,
  setSettingsData,
  setSettingsDataLoading,
  setSettingsDataError
} from '../../../store/slices/gameSlice';
import { shouldRejectEmptierServerRealResults } from '../../../utils/championsLeagueRealResults';

/**
 * Custom hook til at preloade Champions League initial data
 * Dette sikrer at dataen er klar når brugeren åbner Champions League spillet
 * 
 * Bruges på HomePage eller andre steder hvor brugeren kan navigere til CL spillet
 */
export const usePreloadChampionsLeagueData = () => {
  const dispatch = useDispatch();
  
  // Tjek om brugeren er logget ind
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const { pathname } = useLocation();
  const shouldPreload = pathname.startsWith('/champions-league');
  
  // Tjek om data allerede er loaded
  const settingsData = useSelector((state) => state.game.settingsData);
  
  // Preload MY_CHAMPIONS_LEAGUE_GAME_DATA (brugerens eget spil data)
  const { 
    data: myGameData, 
    loading: myGameDataLoading,
    error: myGameDataError 
  } = useQuery(MY_CHAMPIONS_LEAGUE_GAME_DATA, {
    // Kun hent hvis brugeren er logget ind
    skip: !isAuthenticated || !shouldPreload,
    // Fetch policy: cache-first for at undgå unødvendige netværk requests
    fetchPolicy: 'cache-first',
  });

  // Preload CHAMPIONS_LEAGUE_TEAMS (alle hold)
  const { 
    data: teamsData, 
    loading: teamsLoading,
    error: teamsError 
  } = useQuery(CHAMPIONS_LEAGUE_TEAMS, {
    skip: !isAuthenticated || !shouldPreload,
    fetchPolicy: 'cache-first',
  });

  // Preload CHAMPIONS_LEAGUE_SETTINGS (settings og fase låsning)
  const { 
    data: settingsDataFromQuery, 
    loading: settingsLoadingFromQuery,
    error: settingsErrorFromQuery 
  } = useQuery(CHAMPIONS_LEAGUE_SETTINGS, {
    skip: !isAuthenticated || !shouldPreload || (settingsData && Object.keys(settingsData).length > 0),
    fetchPolicy: 'cache-first',
  });

  // Synkroniser settings data med Redux
  useEffect(() => {
    if (!isAuthenticated) return;
    
    dispatch(setSettingsDataLoading(settingsLoadingFromQuery));
    
    if (settingsErrorFromQuery) {
      dispatch(setSettingsDataError(settingsErrorFromQuery.message));
    } else if (settingsDataFromQuery?.championsLeagueSettings) {
      const incoming = settingsDataFromQuery.championsLeagueSettings;
      const prevReal = settingsData?.realResults;
      const nextReal = incoming.realResults;
      const payload = shouldRejectEmptierServerRealResults(nextReal, prevReal)
        ? { ...incoming, realResults: prevReal }
        : incoming;
      dispatch(setSettingsData(payload));

      if (payload.phaseLocks) {
        dispatch(setPhaseLocks(payload.phaseLocks));
      }
    }
  }, [settingsDataFromQuery, settingsLoadingFromQuery, settingsErrorFromQuery, isAuthenticated, settingsData, dispatch]);

  return {
    isLoading: myGameDataLoading || teamsLoading || settingsLoadingFromQuery,
    hasError: myGameDataError || teamsError || settingsErrorFromQuery,
    isDataLoaded: !!myGameData && !!teamsData && !!settingsDataFromQuery,
    // Returnér data så det kan bruges hvis nødvendigt
    data: {
      myGameData,
      teamsData,
      settingsData: settingsDataFromQuery
    }
  };
};

